/**
 * @fileoverview PasswordField — the ONE labelled password input with its show / hide toggle, for every
 * surface in the fleet that asks for a secret (PALM-PHONE-UAT-001 A6, 2026-10-19).
 * @package @puffin-core/auth-shell
 *
 * Measured at 393px on palm-web's Security card: a 32px emoji button under a finger. Seven production sites
 * hand-rolled this input — the change card's private field, login-shell's credential form and reset step,
 * invite-core's account setup, and pos-web's PIN, MFA and signup steps — with two spellings of the glyph
 * (`🙈` / `👁️` as code points and as escapes), three catalogs for the same two words, and three ways of
 * owning the shown / hidden state. This is that input once.
 *
 * ## What it owns
 *
 * - The control's identity and description, the way page-shell's `<FormField>` owns them: `id` on the
 *   control, the label linked by `htmlFor` (itself `<id>-label`), `aria-required` while `required`, and
 *   an `error` shown under the control with `role="alert"` at `<id>-error`, named by `aria-describedby`
 *   and marking the control `aria-invalid`. `describedBy` adds the host's own lines (a requirements
 *   list, a sign-in alert); `invalid` marks the control when the host announces the refusal elsewhere.
 *   auth-shell sits BELOW page-shell (page-shell depends on this package), so it cannot compose
 *   `<FormField>`; like phone-ui's `<PhoneInput>` it is a SELF-LABELLING field primitive that stands
 *   beside `<FormField>`, never inside it.
 * - The toggle: a `type="button"` (it never submits the form), `aria-pressed`, named from this package's
 *   catalog by the KIND of secret (`authShell.passwordField.show` / `.hide`, or `.showPin` / `.hidePin`
 *   for `kind="pin"` — one pair per kind, fleet-wide), drawing `Eye` / `EyeOff` from the closed icon
 *   registry. Its box is the shared
 *   `.password-toggle-btn`, which `forms.css` grows to the touch floor under a coarse pointer (A5).
 * - The shown / hidden state, unless the host holds it: a host that shares one toggle across two fields
 *   (login-shell's reset step) passes `shown` + `onToggleShown` to the field with the toggle and
 *   `toggle={false}` + the same `shown` to the field that mirrors it. A mirror renders no button and no
 *   password wrapper (there is nothing to clear), and a mirror with no `shown` THROWS — React 19 runs no
 *   `propTypes`, so the check lives at render.
 *
 * Classes are the shared ones (CSS-001): `.form-group`, `.form-label`, `.form-required`, `.form-input`,
 * `.form-input-password`, `.form-input-error`, `.form-error`, `.input-password-wrapper`,
 * `.password-toggle-btn` (`packages/shared-styles/css/utilities/forms.css`). The `passwordFieldSingleSource`
 * contract holds this module as the only speller of the wrapper and toggle classes.
 *
 * @module @puffin-core/auth-shell/components/PasswordField
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { resolveLucideIcon } from '@puffin-core/icon-registry';

import { useAuthShellTranslate } from '../hooks/useAuthShellTranslate.js';

/** The glyph's box inside the toggle; the toggle's own box is the stylesheet's. */
const GLYPH_SIZE = 18;

const MIRROR_NEEDS_SHOWN =
  'PasswordField: `toggle={false}` mirrors another field’s visibility, so `shown` is required.';
const CONTROLLED_NEEDS_REPORTER =
  'PasswordField: a controlled `shown` needs `onToggleShown`, which the toggle reports through.';
const UNKNOWN_KIND = 'PasswordField: `kind` is `password` or `pin`.';
const WRITABLE_NEEDS_CHANGE =
  'PasswordField: a control that is not read-only needs `onChange`; a keypad-written control passes `inputProps={{ readOnly: true }}` and no handler.';

/**
 * The toggle's words, by the KIND of secret: ONE pair per kind, in this package's catalog, so a PIN
 * entry in pos-web and one in re-auth name their toggle alike.
 */
const TOGGLE_WORDS = Object.freeze({
  password: Object.freeze({
    show: 'authShell.passwordField.show',
    hide: 'authShell.passwordField.hide',
  }),
  pin: Object.freeze({
    show: 'authShell.passwordField.showPin',
    hide: 'authShell.passwordField.hidePin',
  }),
});

/**
 * Join the defined ids into one `aria-describedby` value.
 * @param {Array<string|undefined>} ids - Candidate ids, in reading order.
 * @returns {string|undefined}
 */
function joinIds(ids) {
  const defined = ids.filter(Boolean);
  return defined.length > 0 ? defined.join(' ') : undefined;
}

/**
 * @param {Object} props
 * @param {string} props.id - the control's id; the label is linked to it, the toggle's test id derives from it
 * @param {React.ReactNode} props.label - the field's visible label
 * @param {string} props.value
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} [props.onChange] - the native change event;
 *   REQUIRED unless the control is read-only (`inputProps.readOnly`: a keypad writes it) — THROWS otherwise
 * @param {string} props.autoComplete - `current-password`, `new-password` or `off` (a PIN)
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.required=false] - marks the label (CSS) and the control (`aria-required`)
 * @param {React.ReactNode} [props.error] - the validation error, announced under the control
 * @param {string} [props.errorTestId] - `data-testid` on the error line
 * @param {React.ReactNode} [props.hint] - helper text under the control, shown while no error shows
 * @param {string} [props.hintTestId] - `data-testid` on the hint line
 * @param {boolean} [props.invalid=false] - `aria-invalid` with no line of its own
 * @param {string} [props.describedBy] - ids of the host's own describing lines
 * @param {boolean} [props.shown] - controlled visibility; with `onToggleShown`
 * @param {() => void} [props.onToggleShown] - the toggle's report when `shown` is controlled
 * @param {boolean} [props.toggle=true] - `false` renders no toggle and mirrors `shown`
 * @param {'password'|'pin'} [props.kind='password'] - the kind of secret, which names the toggle (a PIN says
 *   "Show PIN"); anything else THROWS
 * @param {string} [props.testId] - `data-testid` on the control
 * @param {string} [props.toggleTestId] - `data-testid` on the toggle (default `<id>-visibility`)
 * @param {React.Ref<HTMLInputElement>} [props.inputRef] - the control's ref (an `<input>`)
 * @param {Object} [props.inputProps] - pass-through for the control (`inputMode`, `maxLength`, `pattern`,
 *   `placeholder`, `name`); wired as `<FormField>` wires an element child — the field's `id` and `type` win, a
 *   pass-through `aria-describedby` is kept and the field's appended, an explicit `aria-invalid` /
 *   `aria-required` wins (a sign-in marks every control required and no label)
 * @param {string} [props.inputClassName] - extra classes on the control (a size variant, an alignment)
 * @param {string} [props.className] - extra classes on the field group
 * @returns {React.JSX.Element}
 */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  disabled = false,
  required = false,
  error,
  errorTestId,
  hint,
  hintTestId,
  invalid = false,
  describedBy,
  shown,
  onToggleShown,
  toggle = true,
  kind = 'password',
  testId,
  toggleTestId,
  inputRef,
  inputProps,
  inputClassName,
  className,
}) {
  const t = useAuthShellTranslate();
  const [ownShown, setOwnShown] = useState(false);

  const controlled = typeof shown === 'boolean';
  if (!toggle && !controlled) throw new Error(MIRROR_NEEDS_SHOWN);
  if (toggle && controlled && typeof onToggleShown !== 'function') {
    throw new Error(CONTROLLED_NEEDS_REPORTER);
  }
  // A keypad-written control is read-only and has no change handler to call (React raises no
  // controlled-input warning for a read-only control); every other control needs one.
  if (typeof onChange !== 'function' && inputProps?.readOnly !== true) {
    throw new Error(WRITABLE_NEEDS_CHANGE);
  }
  const isShown = controlled ? shown : ownShown;
  const errorShows = Boolean(error);
  // The hint shows while no error does (`<FormField>`'s order), and describes the control then.
  const hintShows = Boolean(hint) && !errorShows;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const markedInvalid = errorShows || invalid;

  // The same wiring `<FormField>` hands an element child: the field's `id` always wins (the label is
  // linked to it), a pass-through `aria-describedby` is kept and the field's appended, and an
  // `aria-invalid` / `aria-required` the host states explicitly wins over the field's.
  const own = inputProps ?? {};
  const control = (
    <input
      {...own}
      ref={inputRef}
      id={id}
      type={isShown ? 'text' : 'password'}
      className={[
        'form-input',
        toggle ? 'form-input-password' : null,
        markedInvalid ? 'form-input-error' : null,
        inputClassName,
      ]
        .filter(Boolean)
        .join(' ')}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      disabled={disabled}
      aria-required={own['aria-required'] ?? (required ? true : undefined)}
      aria-invalid={own['aria-invalid'] ?? (markedInvalid ? true : undefined)}
      aria-describedby={joinIds([
        own['aria-describedby'],
        errorShows ? errorId : undefined,
        hintShows ? hintId : undefined,
        describedBy,
      ])}
      data-testid={testId}
    />
  );

  if (!Object.prototype.hasOwnProperty.call(TOGGLE_WORDS, kind)) throw new Error(UNKNOWN_KIND);
  const toggleWords = {
    show: t(TOGGLE_WORDS[kind].show),
    hide: t(TOGGLE_WORDS[kind].hide),
  };
  const handleToggle = controlled ? onToggleShown : () => setOwnShown((prev) => !prev);

  return (
    <div className={['form-group', className].filter(Boolean).join(' ')}>
      <label
        htmlFor={id}
        id={`${id}-label`}
        className={required ? 'form-label form-required' : 'form-label'}
      >
        {label}
      </label>
      {toggle ? (
        <div className="input-password-wrapper">
          {control}
          <button
            type="button"
            className="password-toggle-btn"
            aria-pressed={isShown ? 'true' : 'false'}
            aria-label={isShown ? toggleWords.hide : toggleWords.show}
            onClick={handleToggle}
            disabled={disabled}
            data-testid={toggleTestId ?? `${id}-visibility`}
          >
            {resolveLucideIcon(isShown ? 'EyeOff' : 'Eye', { size: GLYPH_SIZE })}
          </button>
        </div>
      ) : (
        control
      )}
      {errorShows ? (
        <span id={errorId} className="form-error" role="alert" data-testid={errorTestId}>
          {error}
        </span>
      ) : null}
      {hintShows ? (
        <span id={hintId} className="form-hint" data-testid={hintTestId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

PasswordField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  autoComplete: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.node,
  errorTestId: PropTypes.string,
  hint: PropTypes.node,
  hintTestId: PropTypes.string,
  invalid: PropTypes.bool,
  describedBy: PropTypes.string,
  shown: PropTypes.bool,
  onToggleShown: PropTypes.func,
  toggle: PropTypes.bool,
  kind: PropTypes.oneOf(['password', 'pin']),
  testId: PropTypes.string,
  toggleTestId: PropTypes.string,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  inputProps: PropTypes.object,
  inputClassName: PropTypes.string,
  className: PropTypes.string,
};

export default PasswordField;
